import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Map, Star, Users, DollarSign, TrendingUp, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { adminApi, publicApi } from '../../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1580541631950-7282082b53ce?w=100';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    tours: 0,
    reviews: 0,
    publishedTours: 0
  });
  const [recentTours, setRecentTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [toursRes, reviewsRes] = await Promise.all([
          adminApi.getTours(),
          publicApi.getReviews()
        ]);
        
        const tours = toursRes.data;
        const reviews = reviewsRes.data;
        
        setStats({
          tours: tours.length,
          reviews: reviews.length,
          publishedTours: tours.filter(t => t.is_published).length
        });
        
        setRecentTours(tours.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { 
      title: 'Total Tours', 
      value: stats.tours, 
      icon: Map, 
      color: 'bg-primary/10 text-primary',
      link: '/admin/tours'
    },
    { 
      title: 'Tours Publicados', 
      value: stats.publishedTours, 
      icon: TrendingUp, 
      color: 'bg-green-100 text-green-600',
      link: '/admin/tours'
    },
    { 
      title: 'Reseñas', 
      value: stats.reviews, 
      icon: Star, 
      color: 'bg-amber-100 text-amber-600',
      link: '/admin/reviews'
    }
  ];

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-serif text-stone-900">Dashboard</h1>
        <p className="text-stone-500 mt-1">Resumen general de Caribbean Groove Tours</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Link key={index} to={stat.link}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-stone-500 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-stone-900">
                      {loading ? '-' : stat.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Tours */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Tours Recientes</CardTitle>
          <Link to="/admin/tours">
            <Button variant="ghost" size="sm" className="text-primary">
              Ver todos <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-stone-100 rounded-lg skeleton" />
              ))}
            </div>
          ) : recentTours.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <p>No hay tours creados aún</p>
              <Link to="/admin/tours/new">
                <Button className="mt-4">Crear primer tour</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentTours.map((tour) => (
                <Link 
                  key={tour.id} 
                  to={`/admin/tours/${tour.id}/edit`}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-stone-50 transition-colors"
                >
                  <img
                    src={getImageUrl(tour.images?.[0])}
                    alt={tour.name}
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-900 truncate">{tour.name}</p>
                    <p className="text-sm text-stone-500">${tour.price} • {tour.duration}</p>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    tour.is_published 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-stone-100 text-stone-500'
                  }`}>
                    {tour.is_published ? 'Publicado' : 'Borrador'}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-6">
          <h3 className="font-semibold text-stone-900 mb-2">Crear Nuevo Tour</h3>
          <p className="text-sm text-stone-500 mb-4">Agrega una nueva excursión al catálogo</p>
          <Link to="/admin/tours/new">
            <Button className="w-full" data-testid="dashboard-create-tour">
              <Map className="w-4 h-4 mr-2" />
              Nuevo Tour
            </Button>
          </Link>
        </Card>
        <Card className="p-6">
          <h3 className="font-semibold text-stone-900 mb-2">Configuración</h3>
          <p className="text-sm text-stone-500 mb-4">WhatsApp, redes sociales y pagos</p>
          <Link to="/admin/settings">
            <Button variant="outline" className="w-full">
              Ir a Configuración
            </Button>
          </Link>
        </Card>
      </div>
    </div>
  );
}
